import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FlaskConical, Menu, X } from "lucide-react";

const NAV_LINKS = [
  { href: "/", label: "Home" },
  { href: "/products", label: "Products" },
  { href: "/about", label: "About" },
  { href: "/faq", label: "FAQ" },
  { href: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      {/* Research disclaimer banner */}
      <div
        data-testid="research-disclaimer-banner"
        className="w-full text-center py-1.5 text-xs tracking-[0.2em] uppercase"
        style={{
          background: 'linear-gradient(90deg, rgba(229,0,255,0.15), rgba(0,240,255,0.15), rgba(229,0,255,0.15))',
          color: 'var(--neon-cyan)',
          borderBottom: '1px solid rgba(0,240,255,0.1)',
          fontFamily: "'Roboto Mono', monospace"
        }}
      >
        For Research Purposes Only - Not for Human Consumption
      </div>

      {/* Main nav */}
      <nav
        data-testid="main-navbar"
        className="sticky top-0 z-50 glass"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <FlaskConical
              className="w-7 h-7 transition-all duration-300 group-hover:drop-shadow-[0_0_8px_rgba(0,240,255,0.5)]"
              style={{ color: 'var(--neon-cyan)' }}
            />
            <div className="flex flex-col leading-none">
              <span
                className="text-xl font-bold tracking-tight"
                style={{ fontFamily: "'Rajdhani', sans-serif", color: 'var(--text-primary)' }}
              >
                APOLLO
              </span>
              <span
                className="text-[10px] tracking-[0.25em] uppercase"
                style={{ color: 'var(--neon-cyan)', fontFamily: "'Roboto Mono', monospace" }}
              >
                COMPOUNDS
              </span>
            </div>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="text-sm tracking-wide transition-all duration-300 hover:drop-shadow-[0_0_6px_rgba(0,240,255,0.4)]"
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 600,
                  color: location.pathname === link.href ? 'var(--neon-cyan)' : 'var(--text-secondary)',
                  letterSpacing: '0.08em',
                }}
              >
                {link.label.toUpperCase()}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-inquiry-btn"
              className="btn-neon px-5 py-1.5 rounded text-sm"
            >
              INQUIRE NOW
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ color: 'var(--neon-cyan)' }}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden glass px-6 pb-6 pt-2"
            style={{ borderTop: '1px solid rgba(0,240,255,0.1)' }}
          >
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                onClick={() => setMobileOpen(false)}
                className="block py-3 text-sm tracking-wide"
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 600,
                  color: location.pathname === link.href ? 'var(--neon-cyan)' : 'var(--text-secondary)',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                {link.label.toUpperCase()}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="btn-neon block text-center mt-4 px-5 py-2 rounded text-sm"
            >
              INQUIRE NOW
            </Link>
          </div>
        )}
      </nav>
    </>
  );
}
